import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  INBOX_NOTIFICATION_TYPE,
  NOTIFICATION_LIST_LIMIT,
  NOTIFICATION_POLL_MS,
  NOTIFICATIONS,
  type InboxNotification,
} from '../constants/notifications'
import { QUERY_KEYS } from '../constants/query'
import { toApiMessage } from '../helpers/api-error'
import {
  absorbNotifications,
  alertForNotifications,
  bindNotificationSession,
  isNotificationSessionPrimed,
  subscribeNotificationRing,
  unlockNotificationSound,
} from '../helpers/notification-alerts'
import { startNotificationStream } from '../helpers/notification-stream'
import {
  fetchNotifications,
  fetchUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../helpers/notifications-api'
import { useAuth } from './useAuth'

const listKey = QUERY_KEYS.notificationsList(NOTIFICATION_LIST_LIMIT)

let streamUsers = 0
let stopStream: (() => void) | null = null

export function useNotificationInbox() {
  const { user } = useAuth()
  const userId = user?.id
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [ringing, setRinging] = useState(false)
  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: () => fetchNotifications(NOTIFICATION_LIST_LIMIT),
    refetchInterval: NOTIFICATION_POLL_MS,
    enabled: Boolean(userId),
  })
  const unreadQuery = useQuery({
    queryKey: QUERY_KEYS.notificationsUnread,
    queryFn: fetchUnreadNotificationCount,
    refetchInterval: NOTIFICATION_POLL_MS,
    enabled: Boolean(userId),
  })
  const items = listQuery.data ?? []
  const unreadCount =
    unreadQuery.data ?? items.filter((item) => !item.readAt).length

  useEffect(() => subscribeNotificationRing(setRinging), [])

  useEffect(() => {
    const unlock = () => unlockNotificationSound()
    window.addEventListener('pointerdown', unlock, { once: true })
    return () => window.removeEventListener('pointerdown', unlock)
  }, [])

  useEffect(() => {
    if (!listQuery.data) return
    const fresh = absorbNotifications(
      listQuery.data,
      isNotificationSessionPrimed(),
    )
    if (!fresh.length) return
    alertForNotifications(fresh)
    if (fresh.some((item) => item.type === INBOX_NOTIFICATION_TYPE.orderAssigned)) {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orders })
    }
  }, [listQuery.data, queryClient])

  useEffect(() => {
    if (!userId) return
    bindNotificationSession(userId)
    const client = queryClient
    streamUsers += 1
    if (streamUsers === 1) {
      stopStream = startNotificationStream({
        onNotification(item) {
          const fresh = absorbNotifications([item], true)
          client.setQueryData<InboxNotification[]>(listKey, (current) => {
            const rows = current ?? []
            if (rows.some((row) => row.id === item.id)) return rows
            return [item, ...rows].slice(0, NOTIFICATION_LIST_LIMIT)
          })
          if (!fresh.length) return
          alertForNotifications(fresh)
          if (item.type === INBOX_NOTIFICATION_TYPE.orderAssigned) {
            void client.invalidateQueries({ queryKey: QUERY_KEYS.orders })
          }
          void client.invalidateQueries({ queryKey: QUERY_KEYS.notificationsUnread })
        },
        onUnreadCount(count) {
          client.setQueryData(QUERY_KEYS.notificationsUnread, count)
        },
      })
    }
    return () => {
      streamUsers -= 1
      if (streamUsers === 0) {
        stopStream?.()
        stopStream = null
      }
    }
  }, [queryClient, userId])

  async function markAllRead() {
    await markAllNotificationsRead()
    const readAt = new Date().toISOString()
    queryClient.setQueryData<InboxNotification[]>(listKey, (current) =>
      (current ?? []).map((item) => (item.readAt ? item : { ...item, readAt })),
    )
    queryClient.setQueryData(QUERY_KEYS.notificationsUnread, 0)
  }

  async function openNotification(item: InboxNotification) {
    if (!item.readAt) {
      const updated = await markNotificationRead(item.id)
      queryClient.setQueryData<InboxNotification[]>(listKey, (current) =>
        (current ?? []).map((row) =>
          row.id === item.id ? (updated ?? { ...row, readAt: new Date().toISOString() }) : row,
        ),
      )
      queryClient.setQueryData<number>(QUERY_KEYS.notificationsUnread, (count) =>
        Math.max(0, (count ?? 1) - 1),
      )
    }
    navigate(NOTIFICATIONS.ordersPath)
  }

  return {
    items,
    unreadCount,
    loading: listQuery.isPending && !listQuery.data,
    error: listQuery.error
      ? toApiMessage(listQuery.error, NOTIFICATIONS.loadError)
      : '',
    ringing,
    markAllRead,
    openNotification,
  }
}
